/* eslint-disable no-empty */
import React from 'react'
import Video from '@bt-react/components/Video'
import PresenterControls, { ButtonStatusState } from '@bt-react/components/PresenterControls'
import PresenterModalManager from '../PresenterModalManager'
import { createLocalVideoTrack, LocalTrack, LocalVideoTrackPublication, LocalVideoTrack, LocalAudioTrack } from 'twilio-video'
import styles from '@bt-react/styles'
import systemPreviewStyles from './SystemPreview.module.scss'
import { SystemPreviewProps, SystemPreviewState } from './types'
import UserCard from '../WebcamScreen/UserCard'
import { debug } from '@bt-react/libs/Logger'
import browser from '@bt-react/libs/browser'
import getVideoConstraints from '../../lib/getVideoConstraints'
import { applyBackground } from '../../lib/virtualBackground'
import { VirtualBackgrounds } from '../VirtualBackgroundPicker/VirtualBackgroundPicker'
import { get720FromCustomImage } from '../../lib/customVirtualBackground'
import audit from '../../lib/audit/audit'

class SystemPreview extends React.Component<SystemPreviewProps, SystemPreviewState> {

  _MAX_MEDIA_CREATION_ATTEMPTS = 3
  _LS_HIDDEN_KEY = 'talks-isHidden'
  _LS_MUTED_KEY = 'talks-isMuted'

  state: SystemPreviewState = {
    hasVideoTrackStarted: false,
    permissions: {
      mic: 'prompt' as PermissionState,
      cam: 'prompt' as PermissionState
    },
    mediaCreationAttempt: 0,
    mediaTrack: undefined,
    isCreatingVideoTrack: false
  }

  async componentDidMount () {

    this.checkAudioSource()

    try {

      await this.getDevicePermissions()

    }
    catch (e) {

      debug('failed to getDevicePermissions')

    }

    try {

      await this.getUserMedia()

    }
    catch (e) {

      debug('failed to getUserMedia local')

    }

  }

  async componentDidUpdate (prevProps: SystemPreviewProps, prevState: SystemPreviewState) {

    const isChrome = browser.isChrome()

    const hasSelectedVideoDeviceChanged = this.props.selectedVideoDeviceId !== prevProps.selectedVideoDeviceId

    if (hasSelectedVideoDeviceChanged || (!this.props.isHidden && typeof this.state.mediaTrack === 'undefined' && !this.state.isCreatingVideoTrack)) {

      if (this.state?.permissions?.cam === 'granted') {

        try {

          this.createLocalVideoTrack()

        }
        catch (e) {}

      }

    }

    if (this.props.isHidden && this.state.mediaTrack) {

      try {
        await this.detachVideoTrack(this.state.mediaTrack)

      }
      catch (e) {

        debug('Failed to detachVideoTrack from component update')

      }

    }


    if(isChrome && this.state.mediaTrack && this.props.background && prevProps.background !== this.props.background ||
      isChrome && this.state.mediaTrack && !prevState.mediaTrack && this.props.background) {
        
        await this.state.mediaTrack.mediaStreamTrack.applyConstraints(await this.getConstraints(!(this.props.background.type === VirtualBackgrounds.NONE)))
        await applyBackground(this.state.mediaTrack, {...this.props.background, customImage: get720FromCustomImage(this.props.background.customImage || '')})

    }

    if (!prevProps.isDialInConnected && this.props.isDialInConnected) this.checkAudioSource()

  }

  componentWillUnmount () {

    try {
      applyBackground(this.state.mediaTrack, {customImage: null, type: VirtualBackgrounds.NONE})
      this.detachTrack(this.state.mediaTrack)

    }
    catch (e) {}

  }

  checkAudioSource () {

    // Microphone is the default and doesn't need setting. It's reasonable that audio source could be `telephone` but disconnected.
    if (this.props.isDialInConnected && this.props.audioSource !== 'telephone') return this.props.setAudioSource('telephone')

  }

  async getConstraints (background = false) {
    return { deviceId: this.props.selectedVideoDeviceId, ... await getVideoConstraints(1, this.props.selectedVideoDeviceId, background)}
  }

  async createLocalVideoTrack () {

    if (this.state?.permissions?.cam !== 'granted') return

    this.setState({ isCreatingVideoTrack: true })

    try {

      await this.detachVideoTrack(this.state.mediaTrack)

    }
    catch (e) {}

    let mediaTrack

    try {
      const cons = await this.getConstraints(!(this.props.background && this.props.background.type === VirtualBackgrounds.NONE))
      mediaTrack = await createLocalVideoTrack(cons)

    }
    catch (e) {

      audit.addEntry('PresenterService', 'SystemPreview', `Error creating local video track: ${e}`)

    }

    if (mediaTrack) {

      try {

        mediaTrack.addListener('started', () => this.handleVideoTrackStart())
        mediaTrack.addListener('stopped', () => this.handleVideoTrackStop())

      }
      catch (e) {

        debug(e.message)

      }

    }

    try {

      await this.setState({ mediaTrack, isCreatingVideoTrack: false })

    }
    catch (e) {}

  }

  handleVideoTrackStart () {

    debug('Video track started')
    this.setState({ hasVideoTrackStarted: true })

  }

  handleVideoTrackStop () {

    if (!this.state.hasVideoTrackStarted) {

      debug('Video track stopped')

    }
    this.setState({ hasVideoTrackStarted: false })

  }

  getDevicePermissions = async () => {

    let micPermission: PermissionState = 'denied'
    let camPermission:  PermissionState = 'denied'

    try {
      // https://github.com/microsoft/TypeScript/issues/33923, TLDR: only chrome supports 'microphone' so TS fails it
      //@ts-ignore
      micPermission = (await navigator.permissions.query({ name: 'microphone' }))?.state

    }
    catch (e) {

      audit.addEntry('PresenterService', 'SystemPreviewMicAndCamSettings', `Unable to retrieve mic permissions. Error: ${e}`)

    }

    try {

      // https://github.com/microsoft/TypeScript/issues/33923, TLDR: only chrome supports 'camera' so TS fails it
      //@ts-ignore
      camPermission = (await navigator.permissions.query({ name: 'camera' }))?.state

    }
    catch (e) {

      audit.addEntry('PresenterService', 'SystemPreviewMicAndCamSettings', `Unable to retrieve cam permissions for user: ${this.props.BTID}. Permissions:
        ${JSON.stringify(this.state.permissions)}. Error: ${e}`)

    }

    this.setState({
      permissions: {
        cam: camPermission,
        mic: micPermission
      }
    })

  }

  hasOutstandingDevicePrompts = () => {

    return [
      this.state.permissions.cam,
      this.state.permissions.mic
    ].some(state => state === 'prompt')

  }

  promptForPermissions = async () => {

    try {

      await navigator.mediaDevices.getUserMedia({
        audio: this.state.permissions.mic === 'prompt',
        video: this.state.permissions.cam === 'prompt'
      })

    }
    catch (e) {

      debug(e.message)

      audit.addEntry('PresenterService', 'SystemPreviewMicAndCamSettings', `${this.props.BTID ?? ''} - ${e.message}`)
      return null

    }

    try {

      await this.getDevicePermissions()

    }
    catch (e) {

      debug('Failed to getDevicePermissions in promptForPermissions')

    }

  }

  /** Ensures that we have permissions and can create tracks */
  getUserMedia = async () => {

    if ([
      this.state.permissions.mic,
      this.state.permissions.cam
    ].every(state => state === 'denied')) return

    try {

      await this.setState({ mediaCreationAttempt: this.state.mediaCreationAttempt + 1 })

    }
    catch (e) {}

    if (this.hasOutstandingDevicePrompts()) {

      try {

        await this.promptForPermissions()

      }
      catch (e) {}

    }

  }

  removeListeners = (mediaTrack?: LocalTrack) => {

    if (!mediaTrack) return

    try {

      mediaTrack.removeListener('started', () => this.handleVideoTrackStart())
      mediaTrack.removeListener('stopped', () => this.handleVideoTrackStop())

    }
    catch (e) {}

  }

  detachTrack = (mediaTrack?: LocalVideoTrack | LocalAudioTrack) => {

    if (!mediaTrack) return

    try {

      mediaTrack.stop()

    }
    catch (e) {}

    try {

      mediaTrack.detach()

    }
    catch (e) {}

    this.removeListeners(mediaTrack)

  }

  detachVideoTrack = async (mediaTrack?: LocalVideoTrack) => {

    if (!mediaTrack) return true

    this.detachTrack(mediaTrack)

    try {

      await this.setState({ mediaTrack: undefined })

    }
    catch (e) {}

  }

  onClickVideo = () => {

    audit.addEntry('PresenterService', 'SystemPreviewVideoClick', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      permissions: this.state.permissions,
      isMuted: this.props.isMuted,
      isHidden: this.props.isHidden,
      videoTrack: !!this.state.mediaTrack
    }))
    if (this.state.permissions.cam !== 'granted') return
    this.props.onClickVideo(this.props.BTID)

  }

  onClickAudio = () => {
    audit.addEntry('PresenterService', 'SystemPreviewAudioClick', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      permissions: this.state.permissions,
      isMuted: this.props.isMuted,
      isHidden: this.props.isHidden,
      videoTrack: !!this.state.mediaTrack
    }))
    if (this.state.permissions.mic !== 'granted') return
    this.props.onClickAudio(this.props.BTID)

  }

  getMicrophoneStatus = (): ButtonStatusState => {

    if (this.props.isMuted) return 'hidden'
    if (this.state.permissions.mic !== 'granted') return 'error'
    return 'connected'

  }

  getTelephoneStatus = (): ButtonStatusState => {

    if (this.props.isDialInConnected) {

      return this.props.isMuted ? 'hidden' : 'connected'

    }

    return 'disconnected'

  }

  getVideoStatus = (): ButtonStatusState => {

    if (this.props.isHidden) return 'hidden'
    if (this.state.permissions.cam !== 'granted') return 'error'
    return 'connected'

  }

  onClickSettings = () => {
    audit.addEntry('PresenterService', 'SystemPreviewSettingsClick', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      permissions: this.state.permissions,
      isMuted: this.props.isMuted,
      isHidden: this.props.isHidden,
      videoTrack: !!this.state.mediaTrack
    }))
    this.props.showSettingsModal()
  }

  render () {

    const microphone = this.getMicrophoneStatus()
    const telephone = this.getTelephoneStatus()
    const video = this.getVideoStatus()

    return (
      <div className={`${styles['flex-container']} ${systemPreviewStyles['system-preview']}`}>
        <div className={`${styles['flex-container']} ${systemPreviewStyles['system-preview-input-feed']}`}>
          {
            this.state.mediaTrack && this.state.hasVideoTrackStarted
              ? <Video
                publication={{
                  track: this.state.mediaTrack,
                  trackName: 'videofeed-system-preview'
                } as unknown as LocalVideoTrackPublication}
                mirrorVideo={true}
                trackSid='video-test'
              />
              : <UserCard
                opaque={true}
                avatarClasses={systemPreviewStyles['system-preview-usercard-avatar']}
                showPresenterDetails={false}
                photoId={this.props.localParticipant ? this.props.localParticipant.photoId : ''}
              />
          }
        </div>
        <div className={`${styles['flex-container']} ${systemPreviewStyles['system-preview-controls']}`}>
          <PresenterControls
            isLocal={true}
            onClickAudio={this.onClickAudio}
            onClickVideo={this.onClickVideo}
            onClickSettings={this.onClickSettings}
            audioSource={this.props.audioSource || 'microphone'}
            buttonStatus={{
              microphone,
              telephone,
              video
            }}
          />
          <PresenterModalManager />
        </div>
      </div>
    )

  }

}

export default SystemPreview
